import { Building2 } from 'lucide-react';
import { type ReactNode } from 'react';

import { type InvolvedAgencySummary } from './utils/involvedAgencyLogic';

interface InvolvedAgencySectionProps {
  summary: InvolvedAgencySummary;
  children: ReactNode;
}

export const InvolvedAgencySection = ({
  summary,
  children,
}: InvolvedAgencySectionProps) => {
  // No wrapper for empty or none state
  if (summary.type === 'none' || summary.primaryReports.length === 0) {
    return children;
  }

  // Subtle styling based on confidence
  const getBorderColor = () => {
    switch (summary.type) {
      case 'received':
        return 'border-green-200';
      case 'single':
        return 'border-blue-200';
      case 'multiple':
      case 'unclear':
        return 'border-amber-200';
      case 'tbd':
        return 'border-gray-200';
      default:
        return 'border-gray-200';
    }
  };

  const getLabelBg = () => {
    switch (summary.type) {
      case 'received':
        return 'bg-green-50';
      case 'single':
        return 'bg-blue-50';
      case 'multiple':
      case 'unclear':
        return 'bg-amber-50';
      case 'tbd':
        return 'bg-gray-50';
      default:
        return 'bg-gray-50';
    }
  };

  const getTextColor = () => {
    switch (summary.type) {
      case 'received':
        return 'text-green-700';
      case 'single':
        return 'text-blue-700';
      case 'multiple':
      case 'unclear':
        return 'text-amber-700';
      case 'tbd':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const getIconColor = () => {
    switch (summary.type) {
      case 'received':
        return 'text-green-500';
      case 'single':
        return 'text-blue-500';
      case 'multiple':
      case 'unclear':
        return 'text-amber-500';
      case 'tbd':
        return 'text-gray-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className={`border ${getBorderColor()} rounded-lg`}>
      {/* Minimal status bar */}
      <div
        className={`${getLabelBg()} px-3 py-1.5 border-b ${getBorderColor()} rounded-t-lg`}
      >
        <div className="flex items-center gap-1.5">
          <Building2 className={`w-3.5 h-3.5 ${getIconColor()}`} />
          <span className={`text-xs ${getTextColor()}`}>{summary.message}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-3 bg-white rounded-b-lg">{children}</div>
    </div>
  );
};
